import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Chip, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, TableCell, Tooltip, Typography } from '@mui/material';
import { MoreVert, NavigateBefore, NavigateNext } from '@mui/icons-material';

const navy = '#213363';

export const tableSx = (minWidth = 820) => ({
    minWidth,
    tableLayout: 'fixed',
    '& .MuiTableCell-root': {
        px: 1.5,
        py: 1.35,
        borderBottomColor: '#e9eef5',
        color: '#344054',
        fontSize: '0.8125rem',
        lineHeight: 1.45,
        verticalAlign: 'middle'
    },
    '& .MuiTableCell-head': {
        height: 46,
        py: 1.15,
        color: '#ffffff',
        fontWeight: 700,
        lineHeight: 1.25,
        whiteSpace: 'nowrap'
    },
    '& .MuiTableRow-root': {
        height: 58
    },
    '& .MuiTableRow-root:hover .MuiTableCell-body': {
        backgroundColor: '#f8fafc'
    }
});

export const stickyActionCellSx = {
    position: 'sticky',
    right: 0,
    zIndex: 2,
    width: 68,
    minWidth: 68,
    maxWidth: 68,
    textAlign: 'center',
    backgroundColor: '#ffffff',
    boxShadow: '-8px 0 12px -12px rgba(33, 51, 99, 0.45)'
};

export const stickyActionHeadSx = {
    ...stickyActionCellSx,
    zIndex: 4,
    color: '#ffffff !important',
    backgroundColor: `${navy} !important`
};

export const formatConfigurationDate = (value) => {
    if (!value) {
        return 'N/A';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return 'N/A';
    }

    return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    }).format(date);
};

export const CodeText = ({ children }) => (
    <Typography
        component="span"
        variant="body2"
        sx={{
            color: '#213363',
            fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", monospace',
            fontSize: '0.78rem',
            fontWeight: 650,
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap'
        }}
    >
        {children || 'N/A'}
    </Typography>
);

CodeText.propTypes = {
    children: PropTypes.node
};

export const ClampedText = ({ children, maxWidth = 220 }) => {
    const value = children || 'N/A';

    return (
        <Tooltip title={value === 'N/A' ? '' : String(value)} placement="top-start" arrow>
            <Typography
                component="span"
                variant="body2"
                sx={{
                    display: '-webkit-box',
                    maxWidth,
                    overflow: 'hidden',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 2,
                    color: 'inherit',
                    fontSize: 'inherit',
                    lineHeight: 1.45,
                    wordBreak: 'break-word'
                }}
            >
                {value}
            </Typography>
        </Tooltip>
    );
};

ClampedText.propTypes = {
    children: PropTypes.node,
    maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

export const AuditCell = ({ by, at }) => (
    <Box sx={{ minWidth: 94 }}>
        <Typography
            variant="body2"
            sx={{
                color: '#344054',
                fontSize: '0.78rem',
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
            }}
        >
            {by || 'SYSTEM'}
        </Typography>
        <Typography variant="caption" sx={{ color: '#667085', display: 'block', mt: 0.25, whiteSpace: 'nowrap' }}>
            {formatConfigurationDate(at)}
        </Typography>
    </Box>
);

AuditCell.propTypes = {
    by: PropTypes.node,
    at: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)])
};

export const BudgetCell = ({ primary, secondary, formatCurrency }) => (
    <Box sx={{ minWidth: 104 }}>
        <Typography variant="body2" sx={{ color: '#213363', fontSize: '0.8rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
            {formatCurrency(primary)}
        </Typography>
        <Typography variant="caption" sx={{ color: '#667085', display: 'block', mt: 0.25, whiteSpace: 'nowrap' }}>
            Balance {formatCurrency(secondary)}
        </Typography>
    </Box>
);

BudgetCell.propTypes = {
    primary: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    secondary: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    formatCurrency: PropTypes.func.isRequired
};

export const ConfigurationStatusChip = ({ active }) => (
    <Chip
        label={active ? 'Active' : 'Inactive'}
        size="small"
        sx={{
            height: 24,
            borderRadius: 1,
            backgroundColor: active ? '#e8f3e8' : '#f7e8e7',
            color: active ? '#25643b' : '#9f2d2d',
            fontSize: '0.75rem',
            fontWeight: 700,
            '& .MuiChip-label': {
                px: 1
            }
        }}
    />
);

ConfigurationStatusChip.propTypes = {
    active: PropTypes.bool
};

export const ConfigurationActionMenu = ({ label = 'record', actions }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const closeMenu = () => setAnchorEl(null);
    const runAction = (action) => {
        closeMenu();
        action.onClick();
    };

    return (
        <>
            <Tooltip title={`Actions for ${label}`}>
                <IconButton
                    size="small"
                    aria-label={`Actions for ${label}`}
                    aria-controls={open ? `configuration-actions-${label}` : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    onClick={(event) => setAnchorEl(event.currentTarget)}
                    sx={{
                        color: '#213363',
                        border: '1px solid #d9e1ec',
                        borderRadius: 1.5,
                        '&:hover': {
                            borderColor: '#213363',
                            backgroundColor: '#eef2f8'
                        }
                    }}
                >
                    <MoreVert fontSize="small" />
                </IconButton>
            </Tooltip>
            <Menu
                id={`configuration-actions-${label}`}
                anchorEl={anchorEl}
                open={open}
                onClose={closeMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                    sx: {
                        minWidth: 168,
                        mt: 0.5,
                        border: '1px solid #e1e7ef',
                        boxShadow: '0 10px 28px rgba(33, 51, 99, 0.14)'
                    }
                }}
            >
                {actions.map((action) => {
                    const ActionIcon = action.icon;
                    return (
                        <MenuItem
                            key={action.label}
                            onClick={() => runAction(action)}
                            sx={{ minHeight: 38, color: action.color || '#344054', fontSize: '0.8125rem' }}
                        >
                            {ActionIcon && (
                                <ListItemIcon sx={{ minWidth: '30px !important', color: 'inherit' }}>
                                    <ActionIcon fontSize="small" />
                                </ListItemIcon>
                            )}
                            <ListItemText primary={action.label} primaryTypographyProps={{ fontSize: '0.8125rem' }} />
                        </MenuItem>
                    );
                })}
            </Menu>
        </>
    );
};

ConfigurationActionMenu.propTypes = {
    label: PropTypes.string,
    actions: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            icon: PropTypes.elementType,
            color: PropTypes.string,
            onClick: PropTypes.func.isRequired
        })
    ).isRequired
};

export const ConfigurationPagination = ({ page, rowsPerPage, total, onPageChange }) => {
    const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
    const first = total === 0 ? 0 : page * rowsPerPage + 1;
    const last = Math.min(total, (page + 1) * rowsPerPage);

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 0.75,
                minHeight: 52,
                px: 1.5,
                borderTop: '1px solid #e9eef5',
                backgroundColor: '#fbfcfe'
            }}
        >
            <Typography variant="body2" sx={{ mr: 0.75, color: '#667085', fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums' }}>
                {first}–{last} of {total}
            </Typography>
            <IconButton size="small" aria-label="Previous page" onClick={() => onPageChange(Math.max(0, page - 1))} disabled={page === 0}>
                <NavigateBefore fontSize="small" />
            </IconButton>
            <Typography variant="caption" sx={{ minWidth: 18, color: '#213363', textAlign: 'center', fontWeight: 700 }}>
                {page + 1}
            </Typography>
            <IconButton
                size="small"
                aria-label="Next page"
                onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
                disabled={page >= totalPages - 1}
            >
                <NavigateNext fontSize="small" />
            </IconButton>
        </Box>
    );
};

ConfigurationPagination.propTypes = {
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired
};

export const EmptyTableRow = ({ colSpan, children }) => (
    <TableCell colSpan={colSpan} align="center" sx={{ height: 132 }}>
        {children}
    </TableCell>
);

EmptyTableRow.propTypes = {
    colSpan: PropTypes.number.isRequired,
    children: PropTypes.node.isRequired
};
